import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../config/axios';
import { Play, Download, ChevronDown, ChevronLeft, ChevronRight, ArrowLeftRight, ClipboardList, FileBarChart2, RefreshCw } from 'lucide-react';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart } from 'recharts';
import './Reports.css';
import { compareItemsByFriendlyDisplay } from '../utils/inventoryDisplay';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';
import FullInventoryReport from './FullInventoryReport';

const TRANSFER_PAGE_SIZE = 12;

const statusBadgeClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'approved' || s === 'completed' || s === 'accepted') return 'rpt-badge rpt-badge-approved';
  if (s === 'rejected' || s === 'declined') return 'rpt-badge rpt-badge-rejected';
  if (s === 'cancelled' || s === 'canceled') return 'rpt-badge rpt-badge-cancelled';
  if (s === 'pending' || s === 'submitted' || s === 'awaiting') return 'rpt-badge rpt-badge-pending';
  return 'rpt-badge rpt-badge-default';
};

const Reports = ({ sites = [], users = [], brands = [], inventory = [], managerMode = false, sitesOverride }) => {
  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', siteId: '', cluster: '', managerId: '', brandId: '', category: '', product: '', periodId: ''
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [chartView, setChartView] = useState('bySite');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [reportTab, setReportTab] = useState('inventory');
  const [transferRows, setTransferRows] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferPage, setTransferPage] = useState(1);

  const managers = users.filter(u => u.role === 'manager');
  const liveCategories = [...new Set((inventory || []).map(i => i.pivotCategory || '').filter(Boolean))];
  const displaySites = (managerMode && sitesOverride) ? sitesOverride : sites;

  const siteOptions = filterOptions?.sites?.length
    ? filterOptions.sites
    : displaySites.map(s => ({ id: s.id, name: s.name, isArchivedOnly: false }));
  const brandOptions = filterOptions?.brands?.length
    ? filterOptions.brands
    : brands.map(b => ({ id: b.id, name: b.name, isArchivedOnly: false }));
  const categoryOptions = filterOptions?.categories?.length ? filterOptions.categories : liveCategories;
  const periodOptions = filterOptions?.periods || [{ id: '__live__', name: 'Current period (live counts)', isLive: true }];
  const clusterOptions = filterOptions?.clusters || [];
  const showNoClusterOption = filterOptions?.hasUnclustered === true;

  const appendFilterParams = (params) => {
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.siteId) params.append('siteId', filters.siteId);
    if (filters.cluster) params.append('cluster', filters.cluster);
    if (filters.managerId) params.append('managerId', filters.managerId);
    if (filters.brandId) params.append('brandId', filters.brandId);
    if (filters.category) params.append('category', filters.category);
    if (filters.product) params.append('product', filters.product);
    if (filters.periodId) params.append('periodId', filters.periodId);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/tenant/reports/filter-options');
        if (!cancelled) setFilterOptions(res.data);
      } catch (err) {
        console.error('Failed to load report filter options', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatSourceLabel = (row) => {
    if (row.source === 'archive') return row.periodName ? `Archive (${row.periodName})` : 'Archive';
    return 'Current';
  };

  const siteLabel = (s) => (s.isArchivedOnly ? `${s.name} (archived)` : s.name);

  const runReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      appendFilterParams(params);
      const res = await axiosInstance.get('/api/tenant/reports/data?' + params.toString());
      setData(res.data);
    } catch (err) {
      console.error(err);
      setData({ rows: [], summary: { totalCounts: 0, totalCountedValue: 0 }, charts: {} });
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      appendFilterParams(params);
      const url = axiosInstance.defaults.baseURL
        ? axiosInstance.defaults.baseURL + '/api/tenant/reports/export-excel?' + params.toString()
        : '/api/tenant/reports/export-excel?' + params.toString();
      const token = localStorage.getItem('token');
      const fetchHeaders = { Authorization: 'Bearer ' + token };
      if (typeof window !== 'undefined' && window.location.host) {
        fetchHeaders['X-Tenant-Host'] = window.location.host;
      }
      const res = await fetch(url, { headers: fetchHeaders });
      if (!res.ok) {
        let errData = null;
        try { errData = await res.json(); } catch (_) {}
        setExportError(
          getFriendlyErrorMessage(
            { response: { status: res.status, data: errData || { code: 'EXPORT_FAILED' } } },
            { kind: 'export' }
          ) || EXPORT_FAILED_MESSAGE
        );
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'Inventory_Report_' + new Date().toISOString().split('T')[0] + '.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      setExportError(getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE);
    } finally {
      setExporting(false);
    }
  };

  const loadTransferReport = async () => {
    setTransferLoading(true);
    try {
      const res = await axiosInstance.get('/api/tenant/reports/transfers');
      setTransferRows(res.data?.rows || []);
      setTransferPage(1);
    } catch (err) {
      console.error(err);
      setTransferRows([]);
      setTransferPage(1);
    } finally {
      setTransferLoading(false);
    }
  };

  useEffect(() => {
    if (reportTab === 'transfers') loadTransferReport();
  }, [reportTab]);

  const transferPageCount = Math.max(1, Math.ceil(transferRows.length / TRANSFER_PAGE_SIZE));
  const pagedTransferRows = useMemo(() => {
    const start = (transferPage - 1) * TRANSFER_PAGE_SIZE;
    return transferRows.slice(start, start + TRANSFER_PAGE_SIZE);
  }, [transferRows, transferPage]);

  useEffect(() => {
    if (transferPage > transferPageCount) setTransferPage(transferPageCount);
  }, [transferPage, transferPageCount]);

  const chartData = data?.charts ? (data.charts[chartView] || []) : [];
  const totalCounts = data?.summary?.totalCounts ?? 0;
  const totalValue = data?.summary?.totalCountedValue ?? 0;

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedRows = () => {
    const rows = data?.rows || [];
    if (!sortColumn) return rows.slice(0, 200);
    return [...rows].sort((a, b) => {
      let aV, bV;
      switch (sortColumn) {
        case 'source': aV = (a.source || '').toLowerCase(); bV = (b.source || '').toLowerCase(); break;
        case 'site': aV = (a.siteName || '').toLowerCase(); bV = (b.siteName || '').toLowerCase(); break;
        case 'manager': aV = (a.managerName || '').toLowerCase(); bV = (b.managerName || '').toLowerCase(); break;
        case 'brand': aV = (a.brandName || '').toLowerCase(); bV = (b.brandName || '').toLowerCase(); break;
        case 'category': aV = (a.category || '').toLowerCase(); bV = (b.category || '').toLowerCase(); break;
        case 'product': {
          const cmp = compareItemsByFriendlyDisplay(
            { name: a.product, itemDescription: a.itemDescription },
            { name: b.product, itemDescription: b.itemDescription }
          );
          if (cmp < 0) return sortDirection === 'asc' ? -1 : 1;
          if (cmp > 0) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        case 'itemDescription': aV = (a.itemDescription || '').toLowerCase(); bV = (b.itemDescription || '').toLowerCase(); break;
        case 'qty1': aV = Number(a.countedQuantity ?? 0); bV = Number(b.countedQuantity ?? 0); break;
        case 'val1': aV = Number(a.countedValue1 ?? 0); bV = Number(b.countedValue1 ?? 0); break;
        case 'qty2': aV = Number(a.countedQuantity2 ?? 0); bV = Number(b.countedQuantity2 ?? 0); break;
        case 'val2': aV = Number(a.countedValue2 ?? 0); bV = Number(b.countedValue2 ?? 0); break;
        case 'date': aV = new Date(a.countDate || 0).getTime(); bV = new Date(b.countDate || 0).getTime(); break;
        case 'person': aV = (a.personName || '').toLowerCase(); bV = (b.personName || '').toLowerCase(); break;
        default: return 0;
      }
      if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
      if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }).slice(0, 200);
  };

  const SortableHeader = ({ column, children }) => (
    <th className="rpt-sortable-th" onClick={() => handleSort(column)} title={'Sort by ' + children}>
      <button type="button" className="rpt-sort-btn">
        <span>{children}</span>
        <ChevronDown size={10} className={'rpt-sort-icon' + (sortColumn === column ? ' active' : '') + (sortColumn === column && sortDirection === 'desc' ? ' desc' : '')} />
      </button>
    </th>
  );

  const fmtNum = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div className="rpt-shell">
      <div className="rpt-section-header">
        <div>
          <h2 className="rpt-section-title">Reports</h2>
          <p className="rpt-section-subtitle">Generate and export inventory reports</p>
        </div>
      </div>

      {exportError && (
        <div className="error" role="alert" style={{ marginBottom: 16 }}>
          <strong>Export failed.</strong> {exportError}
        </div>
      )}

      <div className="rpt-report-type-tabs" role="tablist" aria-label="Report type">
        <button
          type="button"
          role="tab"
          aria-selected={reportTab === 'inventory'}
          className={'rpt-report-type-btn' + (reportTab === 'inventory' ? ' active' : '')}
          onClick={() => setReportTab('inventory')}
        >
          <ClipboardList size={16} /> Inventory counts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={reportTab === 'transfers'}
          className={'rpt-report-type-btn' + (reportTab === 'transfers' ? ' active' : '')}
          onClick={() => setReportTab('transfers')}
        >
          <ArrowLeftRight size={16} /> Stock transfers
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={reportTab === 'full-inventory'}
          className={'rpt-report-type-btn' + (reportTab === 'full-inventory' ? ' active' : '')}
          onClick={() => setReportTab('full-inventory')}
        >
          <FileBarChart2 size={16} /> Full Inventory Report
        </button>
      </div>

      {reportTab === 'full-inventory' && <FullInventoryReport />}

      {reportTab === 'transfers' && (
        <div className="card rpt-data-card">
          <div className="rpt-transfer-header">
            <div>
              <h3 className="rpt-data-title">Stock transfer report</h3>
              <p className="rpt-section-subtitle">
                Current counting period and earlier periods (transfers involving your sites).
              </p>
            </div>
            <button type="button" className="rpt-btn-run" onClick={loadTransferReport} disabled={transferLoading}>
              <RefreshCw size={14} aria-hidden />
              {transferLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <div className="rpt-data-scroll">
            <table className="table rpt-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Status</th>
                  <th>From → To</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th className="rpt-num-cell">Qty1 / Qty2</th>
                  <th className="rpt-num-cell">Value</th>
                  <th>Created</th>
                  <th>Resolved</th>
                  <th>Initiated by</th>
                </tr>
              </thead>
              <tbody>
                {transferRows.length === 0 ? (
                  <tr><td colSpan="10" className="rpt-empty-state">{transferLoading ? 'Loading…' : 'No transfer rows.'}</td></tr>
                ) : (
                  pagedTransferRows.map((r) => (
                    <tr key={r.id} className="rpt-row">
                      <td>
                        <span className={r.isCurrentPeriod ? 'rpt-badge rpt-badge-current' : 'rpt-badge rpt-badge-earlier'}>
                          {r.isCurrentPeriod ? 'Current' : 'Earlier'}
                        </span>
                      </td>
                      <td>
                        <span className={statusBadgeClass(r.status)}>{r.status || '—'}</span>
                      </td>
                      <td>{r.senderSiteName} → {r.recipientSiteName}</td>
                      <td className="rpt-linkish">{r.itemName || '—'}</td>
                      <td>{r.pivotCategory || '—'}</td>
                      <td className="rpt-num-cell">{r.quantity1}{r.quantity2 != null && Number(r.quantity2) !== 0 ? ` / ${r.quantity2}` : ''}</td>
                      <td className="rpt-num-cell">{r.totalValue != null ? fmtNum(r.totalValue) : '—'}</td>
                      <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</td>
                      <td>{r.resolvedAt ? new Date(r.resolvedAt).toLocaleString() : '—'}</td>
                      <td>{r.initiatedByName || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {transferRows.length > 0 && (
            <div className="rpt-pagination">
              <span>
                Showing {Math.min(transferRows.length, (transferPage - 1) * TRANSFER_PAGE_SIZE + 1)}
                –{Math.min(transferRows.length, transferPage * TRANSFER_PAGE_SIZE)} of {transferRows.length} transfers
              </span>
              <div className="rpt-pagination-controls">
                <button
                  type="button"
                  className="rpt-page-btn"
                  aria-label="Previous page"
                  disabled={transferPage <= 1}
                  onClick={() => setTransferPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={16} />
                </button>
                <span>{transferPage}/{transferPageCount}</span>
                <button
                  type="button"
                  className="rpt-page-btn"
                  aria-label="Next page"
                  disabled={transferPage >= transferPageCount}
                  onClick={() => setTransferPage((p) => Math.min(transferPageCount, p + 1))}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {reportTab === 'inventory' && (
        <>
      <div className="rpt-stats-row">
        <div className="rpt-stat-card">
          <span className="rpt-stat-label">Total counts</span>
          <span className="rpt-stat-value rpt-stat-counts">{totalCounts}</span>
        </div>
        <div className="rpt-stat-card">
          <span className="rpt-stat-label">Total counted value</span>
          <span className="rpt-stat-value rpt-stat-value-num">{fmtNum(totalValue)}</span>
        </div>
      </div>

      <div className="card rpt-filters-card">
        <h3 className="rpt-filters-title">Report Filters</h3>
        <div className="rpt-filters-grid">
          <div className="rpt-filter-group">
            <label>Date From</label>
            <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
          </div>
          <div className="rpt-filter-group">
            <label>Date To</label>
            <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
          </div>
          <div className="rpt-filter-group">
            <label>Counting period</label>
            <select value={filters.periodId} onChange={e => setFilters(f => ({ ...f, periodId: e.target.value }))}>
              <option value="">All periods (live + archived)</option>
              {periodOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="rpt-filter-group">
            <label>Site</label>
            <select value={filters.siteId} onChange={e => setFilters(f => ({ ...f, siteId: e.target.value }))}>
              <option value="">All Sites</option>
              {siteOptions.map(s => <option key={s.id} value={s.id}>{siteLabel(s)}</option>)}
            </select>
          </div>
          <div className="rpt-filter-group">
            <label>Cluster</label>
            <select value={filters.cluster} onChange={e => setFilters(f => ({ ...f, cluster: e.target.value }))}>
              <option value="">All clusters</option>
              {showNoClusterOption && <option value="__none__">No cluster</option>}
              {clusterOptions.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {!managerMode && (
            <div className="rpt-filter-group">
              <label>Area Manager</label>
              <select value={filters.managerId} onChange={e => setFilters(f => ({ ...f, managerId: e.target.value }))}>
                <option value="">All Managers</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div className="rpt-filter-group">
            <label>Brand</label>
            <select value={filters.brandId} onChange={e => setFilters(f => ({ ...f, brandId: e.target.value }))}>
              <option value="">All Brands</option>
              {brandOptions.map(b => <option key={b.id} value={b.id}>{b.isArchivedOnly ? `${b.name} (archived)` : b.name}</option>)}
            </select>
          </div>
          <div className="rpt-filter-group">
            <label>Category</label>
            <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
              <option value="">All Categories</option>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="rpt-filter-group">
            <label>Product</label>
            <input type="text" placeholder="Filter by description or vendor item name" value={filters.product} onChange={e => setFilters(f => ({ ...f, product: e.target.value }))} />
          </div>
        </div>
        <div className="rpt-actions">
          <button type="button" className="rpt-btn-run" onClick={runReport} disabled={loading}>
            <Play size={14} />
            {loading ? 'Running...' : 'Run Report'}
          </button>
          <button type="button" className="rpt-btn-export" onClick={exportExcel} disabled={exporting}>
            <Download size={14} />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>

      <div className="card rpt-chart-card">
        <div className="rpt-chart-header">
          <h3 className="rpt-chart-title">Visualisation</h3>
          <div className="rpt-chart-selector">
            <span className="rpt-chart-selector-label">Group by:</span>
            {['bySite', 'byManager', 'byBrand', 'byCategory', 'byProduct'].map(k => (
              <button key={k} type="button" className={'rpt-chart-pill' + (chartView === k ? ' active' : '')} onClick={() => setChartView(k)}>
                {k.replace('by', '')}
              </button>
            ))}
          </div>
        </div>
        {chartData.length > 0 ? (
          <div className="rpt-chart-container">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2E6BE6" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#2E6BE6" stopOpacity={0.55} />
                  </linearGradient>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => {
                  if (v >= 1000000) return '\u00A3' + (v / 1000000).toFixed(1) + 'M';
                  if (v >= 1000) return '\u00A3' + (v / 1000).toFixed(0) + 'K';
                  return '\u00A3' + v;
                }} tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                <Tooltip
                  formatter={(value, name) => [fmtNum(value), name]}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-card)',
                    color: 'var(--color-text)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '12px' }} />
                <Bar yAxisId="left" dataKey="count" fill="url(#barGradient)" name="Nr Counts" radius={[4, 4, 0, 0]} barSize={40} />
                <Area yAxisId="right" type="monotone" dataKey="totalQty" fill="url(#areaGradient)" stroke="#10B981" strokeWidth={2.5} name="Total Value" dot={{ r: 4, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="rpt-empty">No data to display. Run a report with filters.</p>
        )}
      </div>

      {data && (
        <div className="card rpt-data-card">
          <h3 className="rpt-data-title">Report Data</h3>
          <div className="rpt-data-scroll">
            <table className="table rpt-table">
              <thead>
                <tr>
                  <SortableHeader column="source">Source</SortableHeader>
                  <SortableHeader column="site">Site</SortableHeader>
                  <SortableHeader column="manager">Area Manager</SortableHeader>
                  <SortableHeader column="brand">Brand</SortableHeader>
                  <SortableHeader column="category">Category</SortableHeader>
                  <SortableHeader column="product">Item Name</SortableHeader>
                  <SortableHeader column="itemDescription">Item Description</SortableHeader>
                  <SortableHeader column="qty1">Counted Qty</SortableHeader>
                  <SortableHeader column="val1">Counted Qty Value</SortableHeader>
                  <SortableHeader column="qty2">Counted Qty 2</SortableHeader>
                  <SortableHeader column="val2">Counted Qty 2 Value</SortableHeader>
                  <SortableHeader column="date">Count Date</SortableHeader>
                  <SortableHeader column="person">Counted By</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).length === 0 ? (
                  <tr><td colSpan="13" className="rpt-empty-state">No report data. Run a report to see results.</td></tr>
                ) : (
                  getSortedRows().map((r, i) => (
                    <tr key={i} className="rpt-row">
                      <td className="rpt-meta-cell">{formatSourceLabel(r)}</td>
                      <td className="rpt-meta-cell">{r.siteIsArchivedOnly ? `${r.siteName} (archived)` : r.siteName}</td>
                      <td className="rpt-meta-cell">{r.managerName}</td>
                      <td className="rpt-meta-cell">{r.brandName}</td>
                      <td className="rpt-meta-cell">{r.category}</td>
                      <td className="rpt-meta-cell">{r.product}</td>
                      <td className="rpt-meta-cell">{r.itemDescription ?? ''}</td>
                      <td className="rpt-meta-cell rpt-num-cell">{fmtNum(r.countedQuantity)}</td>
                      <td className="rpt-meta-cell rpt-num-cell">{fmtNum(r.countedValue1)}</td>
                      <td className="rpt-meta-cell rpt-num-cell">{fmtNum(r.countedQuantity2)}</td>
                      <td className="rpt-meta-cell rpt-num-cell">{fmtNum(r.countedValue2)}</td>
                      <td className="rpt-meta-cell">{r.countDate ? new Date(r.countDate).toLocaleDateString() : ''}</td>
                      <td className="rpt-meta-cell">{r.personName}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {(data.rows || []).length > 200 && <p className="rpt-truncate-note">Showing first 200 of {data.rows.length} records. Export to Excel for full data.</p>}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Reports;